/**
 * Les designs — les petits motifs qu'on ajoute par-dessus une prestation
 * (surtout sur les ongles), facturés en plus.
 *
 * Un design sans section est utilisable partout ; rattaché à une section, il
 * n'apparaît qu'avec elle à la caisse.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Typography, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Chip, Switch, FormControlLabel, CircularProgress,
  Alert, Tooltip, InputAdornment,
} from '@mui/material';
import {
  Edit, Delete, Add, Search, Visibility, VisibilityOff, Palette,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import {
  designAPI, categoriePrestationAPI, extraireListe,
} from '../../../services/studioAPI';

const FORMULAIRE_VIDE = { nom: '', categorie: '', prix: '', est_actif: true };

const htg = (valeur) => `${Number(valeur || 0).toLocaleString()} HTG`;

const Designs = () => {
  const [designs, setDesigns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [recherche, setRecherche] = useState('');
  const [dialogOuvert, setDialogOuvert] = useState(false);
  const [enEdition, setEnEdition] = useState(false);
  const [formulaire, setFormulaire] = useState(FORMULAIRE_VIDE);

  const charger = async () => {
    try {
      setLoading(true);
      setErreur(null);
      const [reponseDesigns, reponseCategories] = await Promise.all([
        designAPI.getAll(),
        categoriePrestationAPI.getAll(),
      ]);
      setDesigns(extraireListe(reponseDesigns));
      setCategories(extraireListe(reponseCategories));
    } catch (error) {
      const message = error.response?.data?.detail || error.message
        || 'Erreur lors du chargement des designs';
      setErreur(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { charger(); }, []);

  const affiches = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    if (!terme) return designs;
    return designs.filter((design) => design.nom.toLowerCase().includes(terme));
  }, [designs, recherche]);

  const ouvrirDialog = (design = null) => {
    if (design) {
      setFormulaire({
        id: design.id,
        nom: design.nom,
        categorie: design.categorie || '',
        prix: design.prix,
        est_actif: design.est_actif,
      });
      setEnEdition(true);
    } else {
      setFormulaire(FORMULAIRE_VIDE);
      setEnEdition(false);
    }
    setDialogOuvert(true);
  };

  const enregistrer = async () => {
    if (!formulaire.nom.trim()) return toast.error('Le nom est obligatoire');
    if (formulaire.prix === '' || Number(formulaire.prix) < 0) {
      return toast.error('Saisissez un prix valide');
    }

    try {
      const donnees = {
        ...formulaire,
        prix: Number(formulaire.prix),
        // Chaîne vide → null, sinon DRF refuse la clé étrangère
        categorie: formulaire.categorie || null,
      };
      if (enEdition) {
        await designAPI.update(formulaire.id, donnees);
        toast.success('Design modifié');
      } else {
        await designAPI.create(donnees);
        toast.success('Design créé');
      }
      setDialogOuvert(false);
      charger();
    } catch (error) {
      toast.error(
        error.response?.data?.nom?.[0] || error.response?.data?.prix?.[0]
        || error.response?.data?.detail || "Erreur lors de l'enregistrement"
      );
    }
  };

  const basculerStatut = async (design) => {
    try {
      await designAPI.patch(design.id, { est_actif: !design.est_actif });
      toast.success(design.est_actif ? 'Design retiré' : 'Design remis');
      charger();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de statut');
    }
  };

  const supprimer = async (design) => {
    if (!window.confirm(`Supprimer le design « ${design.nom} » ?`)) return;
    try {
      await designAPI.delete(design.id);
      toast.success('Design supprimé');
      charger();
    } catch (error) {
      toast.error('Impossible de supprimer un design déjà vendu — désactivez-le plutôt');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="1.5rem 2.5rem">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Designs</Typography>
          <Typography variant="body2" color="text.secondary">
            Les motifs facturés en supplément d'une prestation
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => ouvrirDialog()}>
          Nouveau design
        </Button>
      </Box>

      {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}

      <Box mb={2}>
        <TextField
          size="small"
          placeholder="Chercher un design"
          value={recherche}
          onChange={(e) => setRecherche(e.target.value)}
          sx={{ maxWidth: 320 }}
          fullWidth
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
          }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Design</TableCell>
              <TableCell>Section</TableCell>
              <TableCell align="right">Prix</TableCell>
              <TableCell align="center">Statut</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {affiches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  <Palette sx={{ fontSize: 48, color: 'text.disabled' }} />
                  <Typography color="text.secondary" mt={1}>
                    {designs.length === 0 ? 'Aucun design enregistré' : 'Aucun design ne correspond'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : affiches.map((design) => (
              <TableRow key={design.id} hover>
                <TableCell><Typography fontWeight={600}>{design.nom}</Typography></TableCell>
                <TableCell>
                  {design.categorie_nom
                    ? <Chip size="small" label={design.categorie_nom} variant="outlined" />
                    : <Chip size="small" label="Toutes sections" color="info" variant="outlined" />}
                </TableCell>
                <TableCell align="right">
                  <Typography fontWeight={600}>{htg(design.prix)}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Chip
                    size="small"
                    label={design.est_actif ? 'Actif' : 'Retiré'}
                    color={design.est_actif ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={design.est_actif ? 'Retirer' : 'Remettre'}>
                    <IconButton size="small" onClick={() => basculerStatut(design)}>
                      {design.est_actif ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Modifier">
                    <IconButton size="small" onClick={() => ouvrirDialog(design)}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Supprimer">
                    <IconButton size="small" color="error" onClick={() => supprimer(design)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOuvert} onClose={() => setDialogOuvert(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{enEdition ? 'Modifier le design' : 'Nouveau design'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Nom"
              value={formulaire.nom}
              onChange={(e) => setFormulaire({ ...formulaire, nom: e.target.value })}
              placeholder="Strass, French, effet marbre..."
              fullWidth
              autoFocus
              required
            />
            <TextField
              label="Section"
              select
              value={formulaire.categorie}
              onChange={(e) => setFormulaire({ ...formulaire, categorie: e.target.value })}
              fullWidth
              helperText="Laissez vide pour proposer ce design dans toutes les sections"
            >
              <MenuItem value="">Toutes les sections</MenuItem>
              {categories.map((categorie) => (
                <MenuItem key={categorie.id} value={categorie.id}>{categorie.nom}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Prix"
              type="number"
              value={formulaire.prix}
              onChange={(e) => setFormulaire({ ...formulaire, prix: e.target.value })}
              fullWidth
              required
              InputProps={{ endAdornment: <InputAdornment position="end">HTG</InputAdornment> }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formulaire.est_actif}
                  onChange={(e) => setFormulaire({ ...formulaire, est_actif: e.target.checked })}
                />
              }
              label="Proposé à la caisse"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOuvert(false)}>Annuler</Button>
          <Button variant="contained" onClick={enregistrer}>
            {enEdition ? 'Enregistrer' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Designs;
